<template>
  <InfuseTable
    :click-permission-id="messagePermissions.GET"
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :search="search"
    :has-more-items="hasMoreItems"
    :refreshable="refreshable"
    :filters="filters"
    :filter-configs="filterConfigs"
    @click-row="(row: Message) => emit('click-row', row)"
    @load="emit('load')"
    @refresh="emit('refresh')"
    @add:filter="(f: Filter) => emit('add:filter', f)"
    @remove:filter="(f: Filter) => emit('remove:filter', f)"
  >
    <template #actions>
      <InfuseSelect
        class="basis-1/5 ml-auto"
        name="date-range"
        size="small"
        :model-value="dateRange"
        @update:model-value="(dr: LogMessageDateRange) => emit('update:dateRange', dr)"
        :options="dateRangeOptions"
        :label="t('e_connector.messages.table.dateRangePlaceholder')"
      />
    </template>

    <template #date="{ data }">
      <div :title="data" class="max-w-[300px] truncate">{{ formatDate(data) }}</div>
    </template>

    <template #messageType="{ data }">
      <InfuseTag :variant="getMessageTypeColor(data.charAt(0).toUpperCase() + data.slice(1).toLowerCase())">
        {{ data }}
      </InfuseTag>
    </template>

    <template #messageVersion="{ data }">
      <div :title="data" class="max-w-[300px] truncate">OCPP-{{ data }}</div>
    </template>

    <template #messageAction="{ data }">
      <InfuseTag :variant="getMessageActionColor(data)">{{ data }}</InfuseTag>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { LogMessageDateRange, Message } from '@infuse/api/e-connector/message';
import { useServerLogColors } from '@infuse/cpms/charging-station/services';
import { messagePermissions } from '@infuse/api/e-connector/message';
import { InfuseSelect, InfuseTable, InfuseTag } from '@infuse/ui/components';
import type { UiTableColumn } from '@infuse/ui/domain';
import { Filter, FilterConfiguration } from '@infuse/ui/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDate } from '@infuse/ui/utils';

interface Emits {
  (e: 'click-row', row: Message): void;
  (e: 'create'): void;
  (e: 'load'): void;
  (e: 'remove', row: Message): void;
  (e: 'refresh'): void;
  (e: 'update:dateRange', dateRange: LogMessageDateRange): void;
  (e: 'add:filter', filter: Filter): void;
  (e: 'remove:filter', filter: Filter): void;
}

const emit = defineEmits<Emits>();

interface Props {
  hasMoreItems: boolean;
  loading?: boolean;
  page?: number;
  refreshable?: boolean;
  rows: Message[];
  searchEnabled: boolean;
  searchQuery?: string;
  dateRange?: LogMessageDateRange;
  filters?: Filter[];
  filterConfigs?: FilterConfiguration[];
}

const props = withDefaults(defineProps<Props>(), {
  hasMoreItems: true,
  loading: false,
  refreshable: false,
  searchEnabled: true,
  searchQuery: '',
  dateRange: LogMessageDateRange.LAST_24_HOURS
});

const { t } = useI18n();
const { getMessageTypeColor, getMessageActionColor } = useServerLogColors();

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('e_connector.common.table_search_all'),
        query: props.searchQuery
      }
);

const dateRangeOptions = [
  { key: t('e_connector.common.dateRange.last24Hours'), value: LogMessageDateRange.LAST_24_HOURS },
  { key: t('e_connector.common.dateRange.last3Days'), value: LogMessageDateRange.LAST_3_DAYS },
  { key: t('e_connector.common.dateRange.lastWeek'), value: LogMessageDateRange.LAST_WEEK },
  { key: t('e_connector.common.dateRange.last3Weeks'), value: LogMessageDateRange.LAST_3_WEEKS },
  { key: t('e_connector.common.dateRange.last6Weeks'), value: LogMessageDateRange.LAST_6_WEEKS }
];

const columns: UiTableColumn[] = [
  {
    name: 'date',
    label: () => t('e_connector.common.message.date'),
    field: 'date',
    bold: true
  },
  {
    name: 'messageType',
    label: () => t('e_connector.common.message.messageType'),
    field: 'messageType'
  },
  {
    name: 'messageVersion',
    label: () => t('e_connector.common.message.ocppVersion'),
    field: 'messageVersion'
  },
  {
    name: 'messageAction',
    label: () => t('e_connector.common.message.messageAction'),
    field: 'messageAction'
  },
  {
    name: 'chargePointName',
    label: () => t('e_connector.common.message.chargePointName'),
    field: 'chargePointName'
  },
  {
    name: 'centralSystemName',
    label: () => t('e_connector.common.message.centralSystemName'),
    field: 'centralSystemName'
  }
];
</script>
